/* @ngInject */

export default (
  $q,
  $http,
  $rootScope,
  CacheFactory,
  logger,
  dataService,
  Lender,
  LenderDocument,
  LENDER
) => {
  let cache = null;
  if (!CacheFactory.get(LENDER.CACHE.STORE.NAME)) {
    cache = CacheFactory(LENDER.CACHE.STORE.NAME, {
      maxAge: LENDER.CACHE.STORE.TTL,
      deleteOnExpire: LENDER.CACHE.STORE.DELETION,
      recycleFreq: LENDER.CACHE.STORE.RECYCLE,
      storageMode: LENDER.CACHE.STORE.STORAGE
    });
  }

  /**
   * Build Lender(s) model(s).
   *
   * @param {Object|Object[]} [lenders] - Lender(s) data.
   *
   * @returns {Lender|Lender[]}
   */
  const build = (lenders) => {
    if (!_.isArray(lenders)) {
      return Lender.Build(lenders);
    }
    return lenders.map((lender) => Lender.Build(lender));
  };

  /**
   * Build Lender's document model(s).
   *
   * @param {Object|Object[]} [documents] - Lender's documents data.
   *
   * @returns {Lender|Lender[]}
   */
  const documentBuild = (documents) => {
    if (!_.isArray(documents)) {
      return LenderDocument.Build(documents);
    }
    return documents.map((document) => LenderDocument.Build(document));
  };

  /**
   * Clear all data from cache.
   */
  const clearCache = () => {
    logger.debug('lendersDataFactory', 'clearCache', 'Clearing all data in the cache');
    cache.removeAll();
  };

  /**
   * Get the available Lender(s).
   *
   * @param {Object} [params]
   * @param {Boolean} [params.ignoreCache] - Ignore cache for this request.
   *
   * @returns {Array}
   */
  const getAvailable = (params) => {
    const ignoreCache = params?.ignoreCache === true;
    const cacheKey = JSON.stringify({
      idAccount: $rootScope.CurrentAccount.id,
      idOrganization: $rootScope.CurrentOrganization.id,
      type: 'lendersAvailable',
      params
    });

    logger.log('lendersDataFactory', 'getAvailable', 'Retrieving data via dataService');
    return dataService.apiRequest(
      cache,
      cacheKey,
      ignoreCache,
      build,
      $http.get,
      LENDER.API.AVAILABLE,
      { params }
    );
  };

  /**
   * Get specific Lender(s).
   *
   * @param {Object} [params]
   * @param {Object[]} [params.idLenders] - Include only Lender(s) with given IDs.
   * @param {Boolean} [params.ignoreCache] - Ignore cache for this request.
   *
   * @returns {Array}
   */
  const getSpecific = (params) => {
    const ignoreCache = params?.ignoreCache === true;
    const cacheKey = JSON.stringify({
      idAccount: $rootScope.CurrentAccount.id,
      idOrganization: $rootScope.CurrentOrganization.id,
      type: 'lendersSpecific',
      params
    });

    logger.log('lendersDataFactory', 'getSpecific', 'Retrieving data via dataService');
    return dataService.apiRequest(
      cache,
      cacheKey,
      ignoreCache,
      build,
      $http.post,
      LENDER.API.SPECIFIC,
      { params }
    );
  };

  /**
   * Get documents.
   *
   * @param {Object} [params]
   * @param {Object[]} [params.idLenders] - Include only Lender's documents with given IDs.
   * @param {Object[]} [params.types] - Include only Lender's documents with given types.
   * @param {Boolean} [params.ignoreCache] - Ignore cache for this request.
   *
   * @returns {Array}
   */
  const getDocuments = (params) => {
    const ignoreCache = params?.ignoreCache === true;
    const cacheKey = JSON.stringify({
      idAccount: $rootScope.CurrentAccount.id,
      idOrganization: $rootScope.CurrentOrganization.id,
      type: 'lenderDocuments',
      params
    });

    logger.log('lendersDataFactory', 'getDocuments', 'Retrieving data via dataService');
    return dataService.apiRequest(
      cache,
      cacheKey,
      ignoreCache,
      documentBuild,
      $http.post,
      LENDER.API.DOCUMENTS,
      { params }
    );
  };

  /**
   * Get calculator.
   *
   * @returns {Object}
   */
  const getCalculator = (doc) => {
    logger.log('lendersDataFactory', 'getCalculator', 'Retrieving data');
    return $http
      .get(doc.url, {
        headers: {
          Pragma: undefined,
          'If-Modified-Since': undefined,
          'Cache-Control': undefined
        },
        params: {},
        responseType: 'arraybuffer',
        transformResponse: (data) => {
          let file;
          if (data) {
            file = new Blob([data], { type: 'application/octet-stream' });
          }
          return {
            response: file
          };
        }
      })
      .then((data) => {
        saveAs(data.data.response, `${doc.name}.${doc.format.toLowerCase()}`);
      });
  };

  /**
   * Get calculator.
   *
   * @returns {Object}
   */
  const getLenderDocument = (doc) => {
    logger.log('lendersDataFactory', 'getLenderDocument', 'Retrieving data');
    return $http
      .get(doc.url, {
        headers: {
          Pragma: undefined,
          'If-Modified-Since': undefined,
          'Cache-Control': undefined
        },
        params: {},
        responseType: 'arraybuffer',
        transformResponse: (data) => {
          let file;
          if (data) {
            file = new Blob([data], { type: 'application/octet-stream' });
          }
          return {
            response: file
          };
        }
      })
      .then((data) => {
        saveAs(data.data.response, `${doc.name}.${doc.format.toLowerCase()}`);
      });
  };

  /**
   * Get maximum borrowing.
   *
   * @param {Object} [params]
   * @param {Object[]} [params.idLenders] - Include only Lender(s) with given IDs.
   * @param {Boolean} [params.ignoreCache] - Ignore cache for this request.
   *
   * @returns {Array}
   */
  const getMaximumBorrowing = (params) => {
    const deferred = $q.defer();
    logger.log('lendersDataFactory', 'getMaximumBorrowing', 'Resolving data from API');
    $http.post(LENDER.API.MAXIMUM_BORROWING, { params }).then(
      (payload) => {
        logger.debug('lendersDataFactory', 'getMaximumBorrowing', 'Resolved data from API');
        deferred.resolve(payload.data);
      },
      (err) => {
        logger.error(
          `lendersDataFactory | getMaximumBorrowing | Error while resolving data ${err}`
        );
        deferred.reject();
      }
    );
    return deferred.promise;
  };

  /**
   * Get LMI.
   *
   * @param {Object} [params]
   * @param {Object[]} [params.idLenders] - Include only Lender(s) with given IDs.
   * @param {Boolean} [params.ignoreCache] - Ignore cache for this request.
   *
   * @returns {Array}
   */
  const getLmi = (params) => {
    const deferred = $q.defer();
    logger.log('lendersDataFactory', 'getLmi', 'Resolving data from API');
    $http.post(LENDER.API.LMI, { params }).then(
      (payload) => {
        logger.debug('lendersDataFactory', 'getLmi', 'Resolved data from API');
        deferred.resolve(payload.data);
      },
      (err) => {
        logger.error(`lendersDataFactory | getLmi | Error while resolving data ${err}`);
        deferred.reject();
      }
    );
    return deferred.promise;
  };

  /**
   * Get residentail estimate.
   *
   * @param {Object} [params]
   * @param {Object[]} [params.idLenders] - Include only Lender(s) with given IDs.
   * @param {Boolean} [params.ignoreCache] - Ignore cache for this request.
   *
   * @returns {Array}
   */
  const getResidentialEstimate = (params) => {
    const deferred = $q.defer();
    logger.log('lendersDataFactory', 'getResidentialEstimate', 'Resolving data from API');
    $http.post(LENDER.API.RESIDENTIAL_ESTIMATE, { params }).then(
      (payload) => {
        logger.debug('lendersDataFactory', 'getResidentialEstimate', 'Resolved data from API');
        deferred.resolve(payload.data);
      },
      (err) => {
        logger.error(
          `lendersDataFactory | getResidentialEstimate | Error while resolving data ${err}`
        );
        deferred.reject();
      }
    );
    return deferred.promise;
  };

  return {
    build,
    clearCache,
    getAvailable,
    getSpecific,
    getDocuments,
    getCalculator,
    getLenderDocument,
    getMaximumBorrowing,
    getLmi,
    getResidentialEstimate
  };
};
